import { Action } from './Action';
import { CompositeState } from './CompositeState';
import { CgsEvent } from './Event';
import { Rule } from './Rule';
import { State } from './State';
import { StateEntry } from './StateEntry';

export class StateMachine extends CompositeState {
  private _states: StateEntry[] = [];
  private _initial: State | null = null;
  private _current: StateEntry | null = null;

  public get states(): StateEntry[] {
    return this._states;
  }

  public getChild(index: number): State {
    return this._states[index].state;
  }

  public get isFinished(): boolean {
    return !!this._current?.state && this._current.state.isFinished;
  }

  public isActive(stateId: string): boolean {
    return !!(this._current?.state?.name === stateId || this._current?.state?.isActive(stateId));
  }

  public findById(id: string): State | null {
    let result: State | null = null;
    result = super.findById(id);
    if (result) {
      return result;
    }

    for (let i = 0; i < this._states.length; ++i) {
      const child = this._states[i].state;
      result = child.findById(id);
      if (result) {
        return result;
      }
    }

    return null;
  }

  public dispatchEvent(event: CgsEvent): void {
    if (this._current) {
      const cnt = this._current.rules.length;
      for (let i = 0; i < cnt; ++i) {
        const rule = this._current.rules[i];
        rule.rule.dispatchEvent(event);
      }

      this._current.state.dispatchEvent(event);

      this._checkRules();
    }
  }

  public addState(state: State): void {
    const e = new StateEntry(state);
    this._states.push(e);
  }

  public addRule(from: State, to: State, rule: Rule): void {
    const e = this._states.find((e) => e.state === from);
    if (e) {
      e.rules.push({ rule, state: to });
    }
  }

  public appendRule(from: State, to: State, rule: Rule): void {
    const e = this._states.find((e) => e.state === from);
    if (e) {
      e.rules = [{ rule, state: to }, ...e.rules];
    }
  }

  setInitialState(state: State): void {
    this._initial = state;
  }

  private _startTransition(state: State, transition: Action | null): void {
    if (this._current) {
      const rules = this._current.rules;
      for (let i = 0; i < rules.length; ++i) {
        rules[i].rule.setActive(false);
      }

      this._current.state.onLeave();
    }

    const current = this._states.find((e) => e.state === state);
    if (current) {
      current.state.onEntering(transition);

      this._current = current;

      const rules = current.rules;
      for (let i = 0; i < rules.length; ++i) {
        rules[i].rule.setActive(true);
      }
    }
  }

  private _switchToState(state: State, transition: Action): void {
    this._startTransition(state, transition);
    state.doEnter();
  }

  public onEnteringImpl(): void {
    if (this._initial) {
      this._startTransition(this._initial, null);
    }
  }

  public onEnterImpl(): void {
    if (this._current) {
      this._current.state.doEnter();
    }
  }

  public onLeaveImpl(): void {
    if (this._current) {
      const rules = this._current.rules;
      for (let i = 0; i < rules.length; ++i) {
        rules[i].rule.setActive(false);
      }

      this._current.state.onLeave();

      this._current = null;
    }
  }

  public updateImpl(dt: number): void {
    if (this._current) {
      this._current.state.update(dt);

      const rules = this._current.rules;
      for (let i = 0; i < rules.length; ++i) {
        rules[i].rule.update(dt);
      }

      this._checkRules();
    }
  }

  private _checkRules(): void {
    if (this._current) {
      for (let i = 0; i < this._current.rules.length; ++i) {
        const rule = this._current.rules[i];
        if (rule.rule.isTriggered) {
          this._switchToState(rule.state, rule.rule.transition);
          break;
        }
      }
    }
  }
}
